import { useCallback, useState } from "react";
import { ArrowRight, FileText, Loader2, Lock, Settings, Tags, Sparkles, X } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { showToast } from "@/other/utils/utils";
import { SETTING_KEYS, type SubcategoryDetailProps, type Subcategory } from "../../constants/settingsStructure";
import { useSettingsContext } from "../../context/SettingsContext";
import { OpenAIKeyModal } from "../../components/OpenAIKeyModal";

import { SMMGeneratorProvider, useSMMGeneratorContext } from "./context/SMMGeneratorContext";
import { ManifestEditor } from "./components/ManifestEditor";
import { TagReviewDialog } from "./components/TagReviewDialog";

const NO_KEY_TITLE = "An OpenAI API key is required to use this feature";

const ManifestEmptyState = ({ onGoToBoardContext }: { onGoToBoardContext: () => void }) => (
  <div className="flex flex-1 min-h-0 mx-6 mb-6 rounded-lg border border-dashed border-border bg-card/50 overflow-hidden">
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-muted text-muted-foreground">
        <FileText className="h-5 w-5" />
      </div>
      <h3 className="text-base font-medium text-foreground">No manifest yet</h3>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
        Your Swivu Marketing Manifest is created from your{" "}
        <span className="font-medium text-foreground">Board Context</span>. Set up Board Context with your business
        details to generate a manifest.
      </p>
      <Button onClick={onGoToBoardContext} className="mt-5 gap-1.5">
        Go to Board Context
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  </div>
);

const SMMGeneratorContent = ({ subcategory }: { subcategory: Subcategory }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    manifestContent,
    isLoading,
    isGenerating,
    isGeneratingTags,
    hasOpenAIKey,
    handleGenerateTags,
    cancelGenerateTags,
  } = useSMMGeneratorContext();

  const { categories, setSelectedCategory, setSelectedSubcategory } = useSettingsContext();

  const navigateToOpenAI = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const navigateToBoardContext = useCallback(() => {
    for (const category of categories) {
      const subcategoryMatch = category.subcategories.find(
        (item) => item.settingId === SETTING_KEYS.BOARD_CONTEXT,
      );
      if (subcategoryMatch) {
        setSelectedCategory(category);
        setSelectedSubcategory({ subcategory: subcategoryMatch, category });
        return;
      }
    }
    showToast({ msg: "Board Context setting not found.", type: "error", duration: 3000 });
  }, [categories, setSelectedCategory, setSelectedSubcategory]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasManifest = Boolean(manifestContent) || isGenerating;

  const tagsDisabled = !hasOpenAIKey || isGeneratingTags || isGenerating;
  const tagsHint = !hasOpenAIKey ? NO_KEY_TITLE : undefined;

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Page header */}
      <div className="shrink-0 p-6 pb-0">
        <div className="mb-5">
          <h2 className="text-2xl font-bold text-foreground mb-1">{subcategory.label}</h2>
          <p className="text-sm text-muted-foreground">{subcategory.description}</p>
        </div>
      </div>

      {!hasOpenAIKey && (
        <div className="shrink-0 mx-6 mb-3 flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-amber-600 dark:text-amber-400">OpenAI API key required</p>
            <p className="mt-1 text-sm text-muted-foreground">
              An API key is needed to generate marketing tags.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={navigateToOpenAI} className="shrink-0">
            <Settings className="mr-1.5 h-4 w-4" />
            Configure
          </Button>
        </div>
      )}

      {hasManifest ? (
        <>
          {/* Generate Tags banner */}
          <div className={cn(
            "shrink-0 mx-6 mb-3 rounded-lg border px-4 py-3",
            isGeneratingTags ? "border-border bg-muted/30" : "border-primary/20 bg-primary/5",
          )}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                  isGeneratingTags ? "bg-muted" : "bg-primary/10",
                )}>
                  {isGeneratingTags ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <Sparkles className="h-4 w-4 text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground leading-tight">
                    {isGeneratingTags ? "Generating Tags..." : "Generate Marketing Tags"}
                  </p>
                  <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                    {isGeneratingTags
                      ? "Analyzing your manifest to derive tags. This may take a moment."
                      : "Derive problem, solution, and meta tags from your manifest."}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isGeneratingTags && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={cancelGenerateTags}
                    className="gap-1.5 text-destructive hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                    Cancel
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleGenerateTags}
                  disabled={tagsDisabled}
                  title={tagsHint}
                  className="gap-1.5"
                >
                  {isGeneratingTags ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Tags className="h-3.5 w-3.5" />
                  )}
                  Generate Tags
                </Button>
              </div>
            </div>
          </div>

          {/* Editor card with integrated sidebar */}
          <div className="flex-1 min-h-0 mx-6 mb-6 rounded-lg border border-border bg-card overflow-hidden">
            <ManifestEditor />
          </div>

          <TagReviewDialog />
        </>
      ) : (
        <ManifestEmptyState onGoToBoardContext={navigateToBoardContext} />
      )}

      <OpenAIKeyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

const SMMGeneratorDetail = ({ subcategory }: SubcategoryDetailProps) => (
  <SMMGeneratorProvider>
    <SMMGeneratorContent subcategory={subcategory} />
  </SMMGeneratorProvider>
);

export default SMMGeneratorDetail;
